import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import GlassPanel from "@/components/GlassPanel";
import ScouterNumber from "@/components/ScouterNumber";
import PolySphere from "@/components/PolySphere";
import api from "@/lib/api";
import { Zap, Activity, Wind, Mountain, Crosshair, ArrowRight } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/cec57503-5f34-4f34-bfc1-e69ae83d457c/images/3844907fef3724f81a818d580ba246ace099a7880e14f59c37ea1357a38ef88c.png";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/ys68irlq_GPS%20Logo%20AI-09.png";

const PILLAR_ICONS = {
  strength: Mountain,
  speed: Wind,
  explosive: Zap,
  stamina: Activity,
  control: Crosshair,
};

const PILLAR_DESC = {
  strength: "Force production. The capacity to move mass.",
  speed: "Pure velocity. Linear and lateral acceleration.",
  explosive: "Power output per millisecond. The first half-second.",
  stamina: "Sustained output. The body under duration.",
  control: "Neural calibration. Stability under stress.",
};

const PILLAR_LETTERS = {
  strength: "S",
  speed: "F",
  explosive: "E",
  stamina: "P",
  control: "C",
};

const TESTS = [
  { key: "deadlift_kg", name: "Deadlift (1RM)", pillar: "Strength (S)" },
  { key: "bench_kg", name: "Benchpress (1RM)", pillar: "Strength (S)" },
  { key: "forty_yard_sec", name: "40-Yard Dash", pillar: "Speed (F)" },
  { key: "shuttle_5_10_5_sec", name: "5-10-5 Shuttle", pillar: "Speed (F)" },
  { key: "vertical_jump_cm", name: "Vertical Jump", pillar: "Explosive (E)" },
  { key: "broad_jump_cm", name: "Broad Jump", pillar: "Explosive (E)" },
  { key: "five_min_shuttle_m", name: "5-Min Shuttle Run", pillar: "Stamina (P)" },
  { key: "mile_sec", name: "1-Mile Run", pillar: "Stamina (P)" },
  { key: "dead_hang_sec", name: "Dead Hang", pillar: "Control (C)" },
  { key: "single_leg_stand_sec", name: "Single-Leg Stand", pillar: "Control (C)" },
];

export default function Home() {
  const [top, setTop] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/leaderboard?limit=5&type=pro").then((r) => setTop(r.data.rows || [])).catch(() => {});
    api.get("/archetypes").then((r) => setArchetypes(r.data.archetypes || [])).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden" data-testid="home-hero">
        {/* Pure black background — no image; only ambient wireframe spheres + grid */}

        {/* Centered wireframe sphere behind text */}
        <div className="absolute right-[-10%] md:right-[2%] top-[6%] opacity-90 pointer-events-none">
          <PolySphere size={620} />
        </div>
        <div className="absolute left-[-15%] bottom-[-10%] opacity-40 pointer-events-none">
          <PolySphere size={420} color="rgba(255,255,255,0.25)" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="gps-tag">SYSTEM ONLINE</span>
            <span className="gps-tag">1 SCORE · 5 PILLARS · 10 TESTS · 32 ARCHETYPES</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="gps-heading text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight"
          >
            WHAT'S YOUR <span className="text-white" style={{ fontStyle: "italic" }}>GPS</span>?
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
            className="text-mute max-w-2xl mt-6 text-base md:text-lg leading-relaxed"
          >
            General Physicality Score — a futuristic global performance platform that measures, scores,
            and ranks human physicality through a standardized 10-test system. Get tested. Discover
            your archetype. Compete globally.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link to="/register" className="gps-btn" data-testid="hero-cta-calculate">
              CALCULATE NOW <ArrowRight size={14} />
            </Link>
            <Link to="/archetypes" className="gps-btn ghost" data-testid="hero-cta-archetypes">
              VIEW ARCHETYPES
            </Link>
          </motion.div>

          {/* HUD STATS — live from /api/stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-16">
            {[
              { label: "ATTEMPTS LOGGED", value: stats?.attempts_logged ?? 0 },
              { label: "ATHLETES SIGNED", value: stats?.athletes_signed ?? 0 },
              { label: "50+ GPS ATHLETES", value: stats?.elite_athletes ?? 0 },
              { label: "PARTNER INSTITUTIONS", value: stats?.partner_gyms ?? 0 },
            ].map((s) => (
              <GlassPanel key={s.label} className="p-5" data-testid={`hud-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                <div className="gps-label">{s.label}</div>
                <div className="text-cyan text-2xl md:text-3xl mt-2">
                  <ScouterNumber value={s.value} />
                </div>
              </GlassPanel>
            ))}
          </div>
        </div>

        {/* Floating reticle */}
        <div className="absolute right-6 md:right-16 bottom-32 hidden lg:block">
          <div className="relative w-32 h-32">
            <div className="radar-rings">
              <div className="r" style={{ width: "100%", height: "100%" }} />
              <div className="r" style={{ width: "65%", height: "65%" }} />
              <div className="r" style={{ width: "35%", height: "35%" }} />
            </div>
            <div className="absolute inset-0 grid place-items-center">
              <div className="gps-mono text-cyan text-[10px] tracking-[0.3em]">TESTING</div>
            </div>
          </div>
        </div>
      </section>

      {/* 5 PILLARS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-pillars">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="gps-tag mb-3">01 / FRAMEWORK</div>
            <h2 className="gps-heading text-3xl md:text-5xl">5 PILLARS</h2>
          </div>
          <div className="hidden md:block text-mute text-sm max-w-md">
            Every physical capability collapses into five binary axes — the foundation of the GPS system.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {["strength", "speed", "explosive", "stamina", "control"].map((k, i) => {
            const Icon = PILLAR_ICONS[k];
            return (
              <GlassPanel key={k} className="p-6 glow-hover" data-testid={`pillar-${k}`}>
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} className="text-cyan" />
                  <span className="gps-mono text-cyan text-xs">0{i + 1}</span>
                </div>
                <div className="gps-heading text-xl">
                  {k.toUpperCase()} <span className="text-white opacity-60">({PILLAR_LETTERS[k]})</span>
                </div>
                <p className="text-mute text-xs mt-3 leading-relaxed">{PILLAR_DESC[k]}</p>
                <div className="hud-divider my-4" />
                <div className="gps-mono text-[10px] text-cyan tracking-[0.2em]">2 TESTS · BINARY AXIS</div>
              </GlassPanel>
            );
          })}
        </div>
      </section>

      {/* 10 TESTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-tests">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="gps-tag mb-3">02 / PROTOCOL</div>
            <h2 className="gps-heading text-3xl md:text-5xl">10 TESTS</h2>
          </div>
          <div className="hidden md:block text-mute text-sm max-w-md">
            Objective. Measurable. Executable in any GPS-certified facility.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {TESTS.map((t, i) => (
            <Link key={t.key} to={`/tests/${t.key}`} className="contents" data-testid={`home-test-${t.key}`}>
              <GlassPanel className="p-4 glow-hover cursor-pointer h-full">
                <div className="gps-mono text-cyan text-[10px] tracking-[0.2em]">
                  T-{String(i + 1).padStart(2, "0")}
                </div>
                <div className="gps-heading text-sm md:text-base mt-2 leading-tight">{t.name.toUpperCase()}</div>
                <div className="text-mute text-[10px] mt-3 gps-mono uppercase tracking-[0.18em]">
                  {t.pillar}
                </div>
              </GlassPanel>
            </Link>
          ))}
        </div>
      </section>

      {/* ARCHETYPE PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-archetypes">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="gps-tag mb-3">03 / IDENTITY</div>
            <h2 className="gps-heading text-3xl md:text-5xl">32 ARCHETYPES</h2>
          </div>
          <Link to="/archetypes" className="gps-btn ghost" data-testid="archetypes-view-all">SEE ALL</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {archetypes.slice(0, 8).map((a) => (
            <Link key={a.code} to={`/archetypes/${a.code}`} className="contents" data-testid={`home-archetype-${a.code}`}>
              <GlassPanel className="p-5 glow-hover cursor-pointer h-full flex flex-col relative">
                <div className="flex items-center justify-between">
                  <span className="gps-mono text-cyan text-xs tracking-[0.3em]">{a.code}</span>
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                </div>
                {a.image_url ? (
                  <div className="relative aspect-square w-full mt-4 grid place-items-center">
                    <img
                      src={a.image_url}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-contain"
                      style={{ filter: "drop-shadow(0 0 12px rgba(0,240,255,0.18))" }}
                    />
                  </div>
                ) : (
                  <div className="aspect-square w-full mt-4 grid place-items-center text-mute gps-mono text-[10px] tracking-[0.25em] opacity-60">
                    NO IMAGE
                  </div>
                )}
                <div className="gps-heading text-2xl mt-4">{a.name.toUpperCase()}</div>
                <div className="text-mute text-xs mt-1">{a.tag}</div>
                {/* GPS watermark — bottom-right corner */}
                <img
                  src="https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/jde3b3my_GPS%20Logo%20AI-09.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute bottom-3 right-3 w-7 h-7 object-contain pointer-events-none"
                  style={{ opacity: 0.55 }}
                  data-testid={`archetype-watermark-${a.code}`}
                />
              </GlassPanel>
            </Link>
          ))}
        </div>
      </section>

      {/* LEADERBOARD PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-leaderboard">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="gps-tag mb-3">04 / RANKINGS</div>
            <h2 className="gps-heading text-3xl md:text-5xl">GLOBAL TOP 5</h2>
          </div>
          <Link to="/leaderboard" className="gps-btn ghost" data-testid="leaderboard-view-all">FULL BOARD</Link>
        </div>

        <GlassPanel className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/15">
                {["RANK", "ATHLETE", "INSTITUTION", "COUNTRY", "GPS", "ARCHETYPE"].map((h) => (
                  <th key={h} className="gps-label text-left p-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4 gps-mono text-cyan">#{String(r.rank).padStart(2, "0")}</td>
                  <td className="p-4">
                    <span className="gps-heading">{r.name}</span>
                  </td>
                  <td className="p-4 text-mute text-xs gps-mono">{r.gym || "—"}</td>
                  <td className="p-4 text-mute text-xs gps-mono">{r.country}</td>
                  <td className="p-4 big-numeric text-cyan text-lg">{r.gps?.toFixed(1)}</td>
                  <td className="p-4 gps-mono text-xs">
                    <span className="text-cyan">{r.archetype?.code}</span>
                    <span className="text-mute ml-2">{r.archetype?.name}</span>
                  </td>
                </tr>
              ))}
              {top.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-mute gps-mono text-xs">NO DATA</td></tr>
              )}
            </tbody>
          </table>
        </GlassPanel>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-24">
        <GlassPanel className="p-10 md:p-16 text-center relative overflow-hidden" scanline>
          <div className="absolute inset-0 grid place-items-center opacity-40 pointer-events-none">
            <PolySphere size={500} />
          </div>
          <div className="relative">
            <img src={LOGO_WHITE} alt="" className="h-14 w-14 mx-auto mb-6 logo-glow" />
            <h3 className="gps-heading text-3xl md:text-5xl">READY TO CHECK YOUR GPS?</h3>
            <p className="text-mute mt-4 max-w-xl mx-auto">
              Enter your test scores. Generate your GPS. Claim your archetype.
            </p>
            <Link to="/register" className="gps-btn mt-8 inline-flex" data-testid="cta-register">
              BEGIN CALCULATION <ArrowRight size={14} />
            </Link>
          </div>
        </GlassPanel>
      </section>
    </div>
  );
}
